/**
 * AccountMenu — Konto-„Ecke-Button": Avatar öffnet ein Dropdown mit Profil,
 * Einstellungen (nur GF) und Abmelden. Von überall in einem Klick erreichbar
 * (Header-Ecke mobil, Sidebar-Fuß auf Desktop).
 */
import { useNavigateGeschuetzt } from '@/hooks/useVerlassenWarnung';
import { useAuth } from '@/context/AuthContext';
import { parseRoles } from '@/config/navigation';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { User, Settings, LogOut, ChevronDown } from 'lucide-react';

const ROLE_LABEL: Record<string, string> = {
  geschaeftsfuehrung: 'Geschäftsführung',
  dispatcher: 'Büro',
  office: 'Büro',
  techniker: 'Monteur',
  tech: 'Monteur',
  monteur: 'Monteur',
};

function initialsOf(name?: string, email?: string): string {
  const base = (name || email || '?').trim();
  const parts = base.split(/\s+/).filter(Boolean);
  const chars = parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2);
  return chars.toUpperCase();
}

interface AccountMenuProps {
  /** 'full' = Avatar + Name + Rolle (Sidebar-Fuß); 'compact' = nur Avatar (Header-Ecke). */
  variant?: 'full' | 'compact';
}

export function AccountMenu({ variant = 'compact' }: AccountMenuProps) {
  const navigate = useNavigateGeschuetzt();
  const { user, logout } = useAuth();
  const roles = parseRoles(user?.role);
  const isGf = roles.includes('geschaeftsfuehrung');
  const roleText = roles.map((r) => ROLE_LABEL[r] || r).join(' · ');
  const initials = initialsOf(user?.name, user?.email);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'full' ? (
          <button className="w-full flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted transition-colors text-left">
            <Avatar className="h-9 w-9 flex-shrink-0">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name || 'Angemeldet'}</div>
              <div className="text-[11px] text-muted-foreground truncate">{roleText || '—'}</div>
            </div>
            <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          </button>
        ) : (
          <button
            aria-label="Konto-Menü"
            className="rounded-full ring-2 ring-primary-foreground/30 hover:ring-primary-foreground/60 transition-all"
          >
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary-foreground text-primary text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
          </button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="text-sm font-medium truncate">{user?.name || 'Angemeldet'}</div>
          {user?.email && <div className="text-xs text-muted-foreground truncate">{user.email}</div>}
          {roleText && <div className="text-[11px] text-muted-foreground mt-0.5">{roleText}</div>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profil')}>
          <User className="w-4 h-4 mr-2" /> Profil
        </DropdownMenuItem>
        {isGf && (
          <DropdownMenuItem onClick={() => navigate('/settings/questions')}>
            <Settings className="w-4 h-4 mr-2" /> Einstellungen
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
          <LogOut className="w-4 h-4 mr-2" /> Abmelden
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
